import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Heart, Loader2 } from "lucide-react";
import apiClient from "../../api/apiClient";
import { useGlobalAlert } from "../../context/AlertContext";

import "./WalkBuddyRating.css";
import rateDogPrintGreen from "../../assets/rateDogPrintGreen.svg";
import rateDogPrintBlue from "../../assets/rateDogPrintBlue.svg";
import socialPawsLogo from "../../assets/appIcon.svg";

import TopNavBar from "../../components/TopNavBar";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";
import MobileTopBar from "../../components/MobileTopBar";

const WalkBuddyRating = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerAlert } = useGlobalAlert();

  const [rating, setRating] = useState(3);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const bookingData = location.state || {};
  const { bookingId, partnerId, partnerName, partnerAvatar, isRequester } =
    bookingData;

  const ratingLabels = [
    { value: 1, title: "1 PAW", sub: "RUFF EXPERIENCE" },
    { value: 2, title: "2 PAWS", sub: "COULD BE BETTER" },
    { value: 3, title: "3 PAWS", sub: "GOOD HANG" },
    { value: 4, title: "4 PAWS", sub: "GREAT COMPANION" },
    { value: 5, title: "5 PAWS", sub: "PACK FAVORITE!" },
  ];

  const trackFillWidths = [0, 25, 50, 75, 100];
  const currentFillPercent = trackFillWidths[rating - 1];

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      triggerAlert("Please share some feedback before submitting.", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        user_id: partnerId,
        booking_id: bookingId,
        review: feedback,
        review_level: rating,
      };

      const response = await apiClient.post("/userReview/create", payload);

      if (response.data?.status) {
        triggerAlert("Review submitted successfully!", "success");
        navigate("/current-bookings", { state: { returnId: bookingId } });
      } else {
        triggerAlert(
          response.data?.message || "Failed to submit review.",
          "error",
        );
      }
    } catch (error) {
      console.error("Submission error:", error);
      triggerAlert("Something went wrong. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="wbr-main-container">
      {/* DESKTOP HEADER */}
      <div
        className="feedback-top-nav wbr-desktop-only"
        style={{ padding: "32px" }}
      >
        <button
          className="back-nav-btn"
          onClick={() => navigate(-1)}
          disabled={isSubmitting}
        >
          <ArrowLeft size={24} color="#000" strokeWidth={3} />
          <span style={{ color: "rgba(0, 0, 0, 0.5)" }}>Back</span>
        </button>
        <div className="header-icons-right">
          <TopNavBar theme="light" />
        </div>
      </div>

      {/* MOBILE HEADER */}
      <div className="cb-custom-top-header cb-mobile-only">
        {/* TOP ROW */}
        {/* <div className="cb-header-top-row">
          <button
            className="cb-custom-back-btn"
            onClick={() => navigate(-1)}
          disabled={isSubmitting}
          >
            <ArrowLeft size={24} color="#fff" strokeWidth={3} />
            <span>Back</span>
          </button>

          <div
            className="cb-custom-brand"
            onClick={() => navigate(-1)}
          >
            <img src={socialPawsLogo} alt="Social Paws" style={{ width: "22px" }} />
            <span>Social Paws</span>
          </div>
        </div> */}
        <MobileTopBar
          onBack={() => navigate(-1)}
          backColor="#fff"
          logoColor="#fff"
        />

        {/* BOTTOM ROW */}
        <div className="cb-header-bottom-row">
          <div className="header-icons-right">
            <TopNavBar theme="dark" />
          </div>
        </div>
      </div>

      <div className="wbr-content">
        <h1 className="wbr-title">RATE YOUR WALK BUDDY!</h1>
        <p className="wbr-subtitle">
          Your pawsitive rating/ review will appear on {partnerName}'s profile
          to help other users get to know them
        </p>

        <div className="wbr-profile-card">
          <img
            src={partnerAvatar || userPlaceholder}
            alt={partnerName}
            className="wbr-partner-img"
          />
          <div className="wbr-profile-label">
            <Heart size={18} strokeWidth={2} />
            <span className="wbr-partner-name">{partnerName}</span>
          </div>
        </div>

        <div className="wbr-slider-wrapper">
          <div className="wbr-slider-track">
            <div className="wbr-track-line"></div>

            <div
              className="wbr-track-fill"
              style={{
                width: `${currentFillPercent}%`,
                "--mobile-fill-height": `${currentFillPercent}%`,
              }}
            ></div>

            {ratingLabels.map((item, index) => (
              <div
                key={item.value}
                className={`wbr-paw-point ${rating >= item.value ? "active" : ""}`}
                onClick={() => !isSubmitting && setRating(item.value)}
                style={{
                  left: `${trackFillWidths[index]}%`,
                  "--mobile-bottom-pos": `${trackFillWidths[index]}%`,
                }}
              >
                <div className="wbr-paw-icon">
                  <img
                    src={
                      rating >= item.value
                        ? rateDogPrintBlue
                        : rateDogPrintGreen
                    }
                    alt="paw icon"
                    className="rate-dog-icon"
                  />
                </div>
                <div className="wbr-label-text">
                  <span className="wbr-label-title">{item.title}</span>
                  <span className="wbr-label-sub">{item.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          className={`wbr-submit-button ${isSubmitting ? "disabled" : ""}`}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            "SUBMIT"
          )}
        </button>

        <div className="wbr-feedback-section">
          <h2 className="wbr-feedback-title">SPILL THE TREATS!</h2>
          <textarea
            className="wbr-feedback-input"
            placeholder="Tell us about the walk..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default WalkBuddyRating;
